import type { ThemeTypes } from '@/types/themeTypes/ThemeType';

const SHARED_COLORS = {
  primaryLightest: '#E5F5FF',
  primary1: '#CCEBFF',
  primary2: '#9ED8FF',
  primary3: '#6BC4FF',
  primary4: '#3DB1FF',
  primary5: '#0A9DFF',
  primary6: '#0085DB',
  primary7: '#0062A3',
  primary8: '#00406B',
  primary9: '#002238',
  primaryDarken: '#000F19',

  greyLightest: '#F8FAFC',
  grey50: '#F8FAFC',
  grey1: '#F2F6FA',
  grey2: '#F0F5F9',
  grey3: '#DFE5EF',
  grey4: '#7C8FAC',
  grey5: '#5A6A85',
  grey6: '#111C2D',
  grey7: '#0D1521',
  grey8: '#080E16',
  grey9: '#04070B',
  greyDarken: '#030507',

  secondaryLightest: '#F4F5F6',
  secondary1: '#E6E8EA',
  secondary2: '#D0D4D7',
  secondary3: '#B8BDC2',
  secondary4: '#9FA6AC',
  secondary5: '#869097',
  secondary6: '#707A82',
  secondary7: '#555C63',
  secondary8: '#393E42',
  secondary9: '#1C1F21',
  secondaryDarken: '#030507',

  successLightest: '#EFFBF5',
  success1: '#E3F8ED',
  success2: '#C2EFD8',
  success3: '#A6E8C6',
  success4: '#A6E8C6',
  success5: '#69D89F',
  success6: '#4BD08B',
  success7: '#2CAA69',
  success8: '#1E7146',
  success9: '#0F3923',
  successDarken: '#071C12',

  infoLightest: '#F1FBFE',
  info1: '#DFF6FC',
  info2: '#C3EEF9',
  info3: '#A3E5F5',
  info4: '#83DBF2',
  info5: '#67D3EF',
  info6: '#46CAEB',
  info7: '#16AACF',
  info8: '#0F718A',
  info9: '#073945',
  infoDarken: '#031A20',

  errorLightest: '#FFF7F5',
  error1: '#FEEFEB',
  error2: '#FEDBD2',
  error3: '#FDCBBF',
  error4: '#FCB7A6',
  error5: '#FCA792',
  error6: '#FB977D',
  error7: '#F84C20',
  error8: '#B72906',
  error9: '#591403',
  errorDarken: '#2D0A01',

  warningLightest: '#FFFBF5',
  warning1: '#FEF4E7',
  warning2: '#FDEBD3',
  warning3: '#FCE0BB',
  warning4: '#FAD4A3',
  warning5: '#F9CB8F',
  warning6: '#FDD835',
  warning7: '#F49820',
  warning8: '#AF6709',
  warning9: '#573304',
  warningDarken: '#2C1A02',

  purpleLightest: '#F4F0FA',
  purple50: '#F4F0FA',
  purple1: '#E9E1F4',
  purple2: '#D0BFE8',
  purple3: '#BAA1DD',
  purple4: '#A280D1',
  purple5: '#8C62C6',
  purple6: '#7543B9',
  purple7: '#57328B',
  purple8: '#3B225E',
  purple9: '#1C102D',
  purpleDarken: '#0E0816',

  amberLightest: '#FFFBEC',
  amber1: '#FEF2CA',
  amber2: '#FDE591',
  amber3: '#FCD15C',
  amber4: '#FABD3D',
  amber5: '#F49C2B',
  amber6: '#D87520',
  amber7: '#B35218',
  amber8: '#913F16',
  amber9: '#773415',
  amberDarken: '#451906',

  limeLightest: '#F7FEE8',
  lime1: '#ECFBCE',
  lime2: '#DAF8A3',
  lime3: '#BFF071',
  lime4: '#A5E44C',
  lime5: '#86CA36',
  lime6: '#67A228',
  lime7: '#4E7B20',
  lime8: '#40611C',
  lime9: '#37521B',
  limeDarken: '#1B2E09',

  tealLightest: '#F0FDFA',
  teal1: '#CDFBF1',
  teal2: '#9BF6E4',
  teal3: '#63EAD5',
  teal4: '#36D4C0',
  teal5: '#20B8A6',
  teal6: '#179488',
  teal7: '#15766E',
  teal8: '#155E59',
  teal9: '#154E4A',
  tealDarken: '#052F2E',

  pinkLightest: '#FDF2F8',
  pink1: '#FCE7F3',
  pink2: '#FACFE7',
  pink3: '#F8A9D3',
  pink4: '#F373B4',
  pink5: '#EA4A97',
  pink6: '#D92876',
  pink7: '#BD195C',
  pink8: '#9C184C',
  pink9: '#821942',
  pinkDarken: '#4F0724',

  whatsappBg: '#f0fdfa',
  whatsappIconBg: '#CDFBF1',
  whatsappPrimary: '#20B8A6',
};

const BLUE_THEME: ThemeTypes = {
  name: 'BLUE_THEME',
  dark: false,
  variables: {
    'border-color': '#e5eaef',
    'border-opacity': 1,
  },
  colors: {
    primary: '#0085db',
    secondary: '#707a82',
    info: '#46caeb',
    success: '#4bd08b',
    warning: '#f9dd58',
    error: '#ff0000',
    orange: '#fb977d',
    indigo: '#7543B9',
    lightprimary: '#e5f3fb',
    lightinfo: '#e1f5fa',
    lightsecondary: '#e7ecf0',
    lightsuccess: '#dffff3',
    lighterror: '#ffede9',
    lightwarning: '#fff6ea',
    lightindigo: '#f1ebff',
    textPrimary: '#2A3547',
    textSecondary: '#2A3547',
    borderColor: '#e5eaef',
    inputBorder: '#aeb2b9',
    containerBg: '#ffffff',
    background: '#F8FAFC',
    hoverColor: '#f6f9fc',
    surface: '#fff',
    grey100: '#707a82',
    grey200: '#111c2d',
    darkbg: '#2a3447',
    bglight: '#f5f8fb',
    bgdark: '#111c2d',
    ...SHARED_COLORS,
  },
};

const AQUA_THEME: ThemeTypes = {
  name: 'AQUA_THEME',
  dark: false,
  variables: {
    'border-color': '#e5eaef',
    'border-opacity': 1,
  },
  colors: {
    primary: '#0074BA',
    secondary: '#47D7BC',
    info: '#46caeb',
    success: '#4bd08b',
    warning: '#f9dd58',
    error: '#ff0000',
    orange: '#fb977d',
    indigo: '#8763da',
    lightprimary: '#e5f3fb',
    lightinfo: '#e1f5fa',
    lightsecondary: '#e7ecf0',
    lightsuccess: '#dffff3',
    lighterror: '#ffede9',
    lightwarning: '#fff6ea',
    lightindigo: '#f1ebff',
    textPrimary: '#2A3547',
    textSecondary: '#2A3547',
    borderColor: '#e5eaef',
    inputBorder: '#aeb2b9',
    containerBg: '#ffffff',
    background: '#F8FAFC',
    hoverColor: '#f6f9fc',
    surface: '#fff',
    grey100: '#707a82',
    grey200: '#111c2d',
    darkbg: '#2a3447',
    bglight: '#f5f8fb',
    bgdark: '#111c2d',
    ...SHARED_COLORS,
  },
};

const PURPLE_THEME: ThemeTypes = {
  name: 'PURPLE_THEME',
  dark: false,
  variables: {
    'border-color': '#e5eaef',
    'border-opacity': 1,
  },
  colors: {
    primary: '#763EBD',
    secondary: '#95CFD5',
    info: '#46caeb',
    success: '#4bd08b',
    warning: '#f9dd58',
    error: '#ff0000',
    orange: '#fb977d',
    indigo: '#8763da',
    lightprimary: '#e5f3fb',
    lightinfo: '#e1f5fa',
    lightsecondary: '#e7ecf0',
    lightsuccess: '#dffff3',
    lighterror: '#ffede9',
    lightwarning: '#fff6ea',
    lightindigo: '#f1ebff',
    textPrimary: '#2A3547',
    textSecondary: '#2A3547',
    borderColor: '#e5eaef',
    inputBorder: '#aeb2b9',
    containerBg: '#ffffff',
    background: '#F8FAFC',
    hoverColor: '#f6f9fc',
    surface: '#fff',
    grey100: '#707a82',
    grey200: '#111c2d',
    darkbg: '#2a3447',
    bglight: '#f5f8fb',
    bgdark: '#111c2d',
    ...SHARED_COLORS,
  },
};

const GREEN_THEME: ThemeTypes = {
  name: 'GREEN_THEME',
  dark: false,
  variables: {
    'border-color': '#e5eaef',
    'border-opacity': 1,
  },
  colors: {
    primary: '#0A7EA4',
    secondary: '#CCDA4E',
    info: '#46caeb',
    success: '#4bd08b',
    warning: '#f8c076',
    error: '#ff0000',
    orange: '#fb977d',
    indigo: '#8763da',
    lightprimary: '#e5f3fb',
    lightinfo: '#e1f5fa',
    lightsecondary: '#e7ecf0',
    lightsuccess: '#dffff3',
    lighterror: '#ffede9',
    lightwarning: '#fff6ea',
    lightindigo: '#f1ebff',
    textPrimary: '#2A3547',
    textSecondary: '#2A3547',
    borderColor: '#e5eaef',
    inputBorder: '#aeb2b9',
    containerBg: '#ffffff',
    background: '#F8FAFC',
    hoverColor: '#f6f9fc',
    surface: '#fff',
    grey100: '#707a82',
    grey200: '#111c2d',
    darkbg: '#2a3447',
    bglight: '#f5f8fb',
    bgdark: '#111c2d',
    ...SHARED_COLORS,
  },
};

const CYAN_THEME: ThemeTypes = {
  name: 'CYAN_THEME',
  dark: false,
  variables: {
    'border-color': '#e5eaef',
    'border-opacity': 1,
  },
  colors: {
    primary: '#01C0C8',
    secondary: '#FB9678',
    info: '#46caeb',
    success: '#4bd08b',
    warning: '#f8c076',
    error: '#ff0000',
    orange: '#fb977d',
    indigo: '#8763da',
    lightprimary: '#e5f3fb',
    lightinfo: '#e1f5fa',
    lightsecondary: '#e7ecf0',
    lightsuccess: '#dffff3',
    lighterror: '#ffede9',
    lightwarning: '#fff6ea',
    lightindigo: '#f1ebff',
    textPrimary: '#2A3547',
    textSecondary: '#2A3547',
    borderColor: '#e5eaef',
    inputBorder: '#aeb2b9',
    containerBg: '#ffffff',
    background: '#F8FAFC',
    hoverColor: '#f6f9fc',
    surface: '#fff',
    grey100: '#707a82',
    grey200: '#111c2d',
    darkbg: '#2a3447',
    bglight: '#f5f8fb',
    bgdark: '#111c2d',
    ...SHARED_COLORS,
  },
};

const ORANGE_THEME: ThemeTypes = {
  name: 'ORANGE_THEME',
  dark: false,
  variables: {
    'border-color': '#e5eaef',
    'border-opacity': 1,
  },
  colors: {
    primary: '#FA896B',
    secondary: '#0074BA',
    info: '#46caeb',
    success: '#4bd08b',
    warning: '#f8c076',
    error: '#ff0000',
    orange: '#fb977d',
    indigo: '#8763da',
    lightprimary: '#e5f3fb',
    lightinfo: '#e1f5fa',
    lightsecondary: '#e7ecf0',
    lightsuccess: '#dffff3',
    lighterror: '#ffede9',
    lightwarning: '#fff6ea',
    lightindigo: '#f1ebff',
    textPrimary: '#2A3547',
    textSecondary: '#2A3547',
    borderColor: '#e5eaef',
    inputBorder: '#aeb2b9',
    containerBg: '#ffffff',
    background: '#F8FAFC',
    hoverColor: '#f6f9fc',
    surface: '#fff',
    grey100: '#707a82',
    grey200: '#111c2d',
    darkbg: '#2a3447',
    bglight: '#f5f8fb',
    bgdark: '#111c2d',
    ...SHARED_COLORS,
  },
};

export {
  AQUA_THEME,
  BLUE_THEME,
  CYAN_THEME,
  GREEN_THEME,
  ORANGE_THEME,
  PURPLE_THEME,
};
